import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronRight,
  Compass,
  BarChart3,
  Calendar,
  Users,
  Brain,
  Sparkles,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  Clock,
  Mail
} from 'lucide-react';

/* ─── colour tokens ─── */
const C = {
  gold: '#C89B3C',
  goldLight: '#E8C872',
  beige: '#F4EFE6',
  charcoal: '#2B2B2B',
  jade: '#5B8C6B',
  jadeMuted: '#4A7A5A',
  dark: '#1A1A1A',
  card: '#232323',
  cardBorder: '#353535',
};

/* ─── Countdown Timer ─── */
const CountdownTimer = () => {
  const launchDate = new Date(Date.now() + 14 * 86400000);
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = launchDate - now;
      if (diff <= 0) return;
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex gap-4 md:gap-6 justify-center" data-testid="countdown-timer">
      {Object.entries(time).map(([label, val]) => (
        <div key={label} className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl font-bold"
            style={{ background: `${C.gold}15`, color: C.gold, border: `1px solid ${C.gold}30` }}>
            {String(val).padStart(2, '0')}
          </div>
          <p className="text-xs mt-2 uppercase tracking-widest" style={{ color: '#888' }}>{label}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Pillar Card (Sneak Peek) ─── */
const PillarCard = ({ stem, branch, label, element, color }) => (
  <div className="relative group cursor-pointer" data-testid={`pillar-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="rounded-xl p-5 transition-all duration-500 group-hover:-translate-y-1"
      style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>{label}</p>
      <div className="text-center mb-3">
        <span className="text-4xl font-serif" style={{ color }}>{stem}</span>
      </div>
      <p className="text-white font-medium text-sm text-center">{branch}</p>
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-xs" style={{ color: '#999' }}>{element}</span>
      </div>
    </div>
    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      style={{ boxShadow: `0 0 30px ${color}20` }} />
  </div>
);

/* ─── Feature Card ─── */
const FeatureCard = ({ icon: Icon, title, desc, accent }) => (
  <Card className="border-0 group cursor-pointer hover:-translate-y-1 transition-all duration-500"
    style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}
    data-testid={`feature-${title.toLowerCase().replace(/\s/g,'-')}`}>
    <CardContent className="p-6">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}15` }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#999' }}>{desc}</p>
    </CardContent>
  </Card>
);

/* ─── Profile Bar ─── */
const ProfileBar = ({ name, pct, color }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-xs">
      <span style={{ color: '#bbb' }}>{name}</span>
      <span style={{ color }}>{pct}%</span>
    </div>
    <div className="h-1.5 rounded-full" style={{ background: '#333' }}>
      <div className="h-full rounded-full transition-all duration-1000"
        style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
    </div>
  </div>
);

/* ─── Main Page ─── */
const BaZiComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'bazi' })
      });
      const data = await res.json();
      setSubmitMsg(data.message || "You're on the list!");
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      setSubmitMsg("Something went wrong. Please try again.");
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: C.dark, color: C.beige }}>

      {/* ─── Subtle BG Pattern ─── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]"
          style={{ background: `radial-gradient(circle, ${C.gold}, transparent 70%)` }} />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.02]"
          style={{ background: `radial-gradient(circle, ${C.jade}, transparent 70%)` }} />
        {/* Compass lines */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.015]">
          <circle cx="50%" cy="30%" r="300" fill="none" stroke={C.gold} strokeWidth="0.5" />
          <circle cx="50%" cy="30%" r="250" fill="none" stroke={C.gold} strokeWidth="0.3" />
          <circle cx="50%" cy="30%" r="200" fill="none" stroke={C.gold} strokeWidth="0.3" />
        </svg>
      </div>

      {/* ─── NAV ─── */}
      <nav className="relative z-10 border-b" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="w-6 h-6" style={{ color: C.gold }} />
            <span className="font-semibold text-white text-lg tracking-wide">BaZi<span style={{ color: C.gold }}>.live</span></span>
          </div>
          <a href="#waitlist">
            <Button className="text-sm font-medium px-5"
              style={{ background: C.gold, color: C.charcoal }}>
              Join Waitlist
            </Button>
          </a>
        </div>
      </nav>

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative z-10 pt-20 pb-28 md:pt-28 md:pb-36" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
            style={{ background: `${C.gold}12`, border: `1px solid ${C.gold}30` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: C.gold }} />
            <span className="text-xs tracking-widest uppercase" style={{ color: C.gold }}>Coming Soon</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
            Your Destiny Is Written<br className="hidden sm:block" /> in Your Life (生).
            <br />
            <span className="font-serif italic" style={{ color: C.goldLight }}>
              We're Building the Tool to Decode It.
            </span>
          </h1>

          <p className="text-base md:text-lg max-w-2xl mx-auto mb-4 leading-relaxed" style={{ color: '#999' }}>
            Discover your BaZi chart, Luck Pillars, Monthly Energy, and Personality Profiles
            through a powerful modern analysis platform.
          </p>

          <p className="text-sm max-w-xl mx-auto mb-10" style={{ color: '#777' }}>
            Our platform is launching soon. Be the first to access the future of BaZi analysis.
          </p>

          {/* CTA */}
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium"
                style={{ background: C.gold, color: C.charcoal }}>
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium border"
                style={{ background: 'transparent', color: C.beige, borderColor: C.cardBorder }}>
                <Mail className="w-4 h-4 mr-2" />
                Notify Me When Live
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════ VISUAL HERO — Dashboard Preview ═══════════════ */}
      <section className="relative z-10 pb-24" data-testid="dashboard-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl p-1" style={{ background: `linear-gradient(135deg, ${C.gold}25, ${C.cardBorder}, ${C.jade}20)` }}>
            <div className="rounded-xl p-6 md:p-8" style={{ background: C.dark }}>
              {/* Dashboard Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <a href="https://lifetune-lab.preview.emergentagent.com/" target="_blank" rel="noopener noreferrer" className="text-white font-semibold text-lg hover:underline">八字排盘系统</a>
                  <p className="text-xs" style={{ color: '#666' }}>BaZi Four Pillars of Destiny Plotter</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ background: `${C.jade}20` }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.jade }} />
                  <span className="text-xs" style={{ color: C.jade }}>Live Analysis</span>
                </div>
              </div>

              {/* Four Pillars Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <PillarCard label="Hour Pillar" stem="丁" branch="Ding – Yin Fire" element="Fire" color="#E85D47" />
                <PillarCard label="Day Pillar" stem="甲" branch="Jia – Yang Wood" element="Wood" color={C.jade} />
                <PillarCard label="Month Pillar" stem="辛" branch="Xin – Yin Metal" element="Metal" color="#C0C0C0" />
                <PillarCard label="Year Pillar" stem="壬" branch="Ren – Yang Water" element="Water" color="#4A90D9" />
              </div>

              {/* Dashboard Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Day Master */}
                <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                  <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Day Master</p>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-serif"
                      style={{ background: `${C.jade}20`, color: C.jade }}>甲</div>
                    <div>
                      <p className="text-white font-semibold">Jia Wood</p>
                      <p className="text-xs" style={{ color: '#888' }}>Yang Wood • Strong</p>
                    </div>
                  </div>
                </div>

                {/* Luck Pillar */}
                <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                  <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Current Luck Pillar</p>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-serif"
                      style={{ background: `${C.gold}15`, color: C.gold }}>乙卯</div>
                    <div>
                      <p className="text-white font-semibold">Yin Wood Rabbit</p>
                      <p className="text-xs" style={{ color: '#888' }}>2024 – 2034</p>
                    </div>
                  </div>
                </div>

                {/* Monthly */}
                <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                  <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Monthly Influence</p>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-serif"
                      style={{ background: '#C0C0C015', color: '#C0C0C0' }}>辛卯</div>
                    <div>
                      <p className="text-white font-semibold">Yin Metal Rabbit</p>
                      <p className="text-xs" style={{ color: '#888' }}>March 2026</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom row: Profiles + Structures */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* 10 Profiles */}
                <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                  <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>10 Profiles Strength</p>
                  <div className="space-y-3">
                    <ProfileBar name="Director (Direct Wealth)" pct={85} color={C.gold} />
                    <ProfileBar name="Performer (Eating God)" pct={72} color="#E85D47" />
                    <ProfileBar name="Leader (7 Killings)" pct={65} color="#4A90D9" />
                    <ProfileBar name="Artist (Hurting Officer)" pct={58} color={C.jade} />
                    <ProfileBar name="Friend (Rob Wealth)" pct={42} color="#C0C0C0" />
                  </div>
                </div>

                {/* Five Structures Radar (simplified) */}
                <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                  <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Five Structures</p>
                  <div className="flex items-center justify-center mb-4">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      {/* Pentagon grid */}
                      {[80, 60, 40, 20].map((r, i) => (
                        <polygon key={i}
                          points={[0,1,2,3,4].map(j => {
                            const a = (j * 72 - 90) * Math.PI / 180;
                            return `${100 + r * Math.cos(a)},${100 + r * Math.sin(a)}`;
                          }).join(' ')}
                          fill="none" stroke={C.cardBorder} strokeWidth="0.5" />
                      ))}
                      {/* Data polygon */}
                      <polygon
                        points={[75,55,50,60,70].map((v, j) => {
                          const r = v * 0.8;
                          const a = (j * 72 - 90) * Math.PI / 180;
                          return `${100 + r * Math.cos(a)},${100 + r * Math.sin(a)}`;
                        }).join(' ')}
                        fill={`${C.gold}20`} stroke={C.gold} strokeWidth="1.5" />
                      {/* Labels */}
                      {[
                        { n: 'Manager', v: 75, x: 100, y: 8 },
                        { n: 'Connector', v: 55, x: 185, y: 62 },
                        { n: 'Thinker', v: 50, x: 155, y: 165 },
                        { n: 'Supporter', v: 60, x: 45, y: 165 },
                        { n: 'Creator', v: 70, x: 15, y: 62 },
                      ].map((l, i) => (
                        <text key={i} x={l.x} y={l.y} textAnchor="middle"
                          fill="#999" fontSize="10">{l.n}</text>
                      ))}
                    </svg>
                  </div>
                  <p className="text-center text-xs" style={{ color: '#777' }}>
                    Primary: <span className="text-white">Manager</span> &bull; Secondary: <span className="text-white">Creator</span>
                  </p>
                </div>
              </div>

              {/* Luck Pillars 大運 */}
              <div className="rounded-xl p-5 mt-4 overflow-hidden" style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                <p className="text-xs uppercase tracking-widest mb-5" style={{ color: '#777' }}>Luck Pillars 大運</p>
                <div className="flex gap-1.5 overflow-x-auto pb-2">
                  {[
                    { age: '2-11', badge: 'EG', badgeColor: '#4CAF50', stem: '己', stemLabel: '食神', pinyin: 'Ji', element: 'Yin Earth', stemColor: '#8B7355',
                      branch: '未', animal: 'Goat', branchEl: 'Earth', branchColor: '#8B7355', active: false,
                      hidden: [{ ch: '己', py: 'Ji', badge: 'EG', bc: '#4CAF50', pct: 60 }, { ch: '丁', py: 'Ding', badge: 'F', bc: '#FF5722', pct: 25 }, { ch: '乙', py: 'Yi', badge: 'IR', bc: '#E91E63', pct: 15 }] },
                    { age: '12-21', badge: 'HO', badgeColor: '#FF9800', stem: '戊', stemLabel: '伤官', pinyin: 'Wu', element: 'Yang Earth', stemColor: '#8B7355',
                      branch: '午', animal: 'Horse', branchEl: 'Earth', branchColor: '#8B7355', active: false,
                      hidden: [{ ch: '丁', py: 'Ding', badge: 'F', bc: '#FF5722', pct: 60 }, { ch: '己', py: 'Ji', badge: 'EG', bc: '#4CAF50', pct: 25 }] },
                    { age: '22-31', badge: 'F', badgeColor: '#FF5722', stem: '丁', stemLabel: '比肩', pinyin: 'Ding', element: 'Yin Fire', stemColor: '#E85D47',
                      branch: '巳', animal: 'Snake', branchEl: 'Fire', branchColor: '#E85D47', active: false,
                      hidden: [{ ch: '丙', py: 'Bing', badge: 'RW', bc: '#2196F3', pct: 60 }, { ch: '戊', py: 'Wu', badge: 'HO', bc: '#FF9800', pct: 25 }, { ch: '庚', py: 'Geng', badge: 'DW', bc: '#607D8B', pct: 15 }] },
                    { age: '32-41', badge: 'RW', badgeColor: '#2196F3', stem: '丙', stemLabel: '劫财', pinyin: 'Bing', element: 'Yang Fire', stemColor: '#E85D47',
                      branch: '辰', animal: 'Dragon', branchEl: 'Fire', branchColor: '#E85D47', active: false,
                      hidden: [{ ch: '戊', py: 'Wu', badge: 'HO', bc: '#FF9800', pct: 60 }, { ch: '乙', py: 'Yi', badge: 'IR', bc: '#E91E63', pct: 25 }, { ch: '癸', py: 'Gui', badge: '7K', bc: '#9C27B0', pct: 15 }] },
                    { age: '42-51', badge: 'IR', badgeColor: '#E91E63', stem: '乙', stemLabel: '偏印', pinyin: 'Yi', element: 'Yin Wood', stemColor: C.jade,
                      branch: '卯', animal: 'Rabbit', branchEl: 'Wood', branchColor: C.jade, active: true,
                      hidden: [{ ch: '乙', py: 'Yi', badge: 'IR', bc: '#E91E63', pct: 60 }] },
                    { age: '52-61', badge: 'DR', badgeColor: '#00BCD4', stem: '甲', stemLabel: '正印', pinyin: 'Jia', element: 'Yang Wood', stemColor: C.jade,
                      branch: '寅', animal: 'Tiger', branchEl: 'Wood', branchColor: C.jade, active: false,
                      hidden: [{ ch: '甲', py: 'Jia', badge: 'DR', bc: '#00BCD4', pct: 60 }, { ch: '丙', py: 'Bing', badge: 'RW', bc: '#2196F3', pct: 25 }, { ch: '戊', py: 'Wu', badge: 'HO', bc: '#FF9800', pct: 15 }] },
                    { age: '62-71', badge: '7K', badgeColor: '#9C27B0', stem: '癸', stemLabel: '七杀', pinyin: 'Gui', element: 'Yin Water', stemColor: '#4A90D9',
                      branch: '丑', animal: 'Ox', branchEl: 'Water', branchColor: '#4A90D9', active: false,
                      hidden: [{ ch: '己', py: 'Ji', badge: 'EG', bc: '#4CAF50', pct: 60 }, { ch: '癸', py: 'Gui', badge: '7K', bc: '#9C27B0', pct: 25 }, { ch: '辛', py: 'Xin', badge: 'IW', bc: '#795548', pct: 15 }] },
                    { age: '72-81', badge: 'DO', badgeColor: '#607D8B', stem: '壬', stemLabel: '正官', pinyin: 'Ren', element: 'Yang Water', stemColor: '#4A90D9',
                      branch: '子', animal: 'Rat', branchEl: 'Water', branchColor: '#4A90D9', active: false,
                      hidden: [{ ch: '癸', py: 'Gui', badge: '7K', bc: '#9C27B0', pct: 60 }] },
                  ].map((lp, i) => (
                    <div key={i} className="flex-shrink-0 w-[120px] rounded-lg overflow-hidden transition-all"
                      style={{ border: lp.active ? `2px solid ${C.gold}60` : `1px solid #333`, background: lp.active ? `${C.gold}08` : '#2a2a2a' }}>
                      {/* Age header */}
                      <div className="py-1.5 text-center text-xs font-medium"
                        style={{ background: lp.active ? `${C.gold}25` : '#3a3a3a', color: lp.active ? C.gold : '#aaa' }}>
                        {lp.age}
                      </div>
                      {/* Heavenly Stem */}
                      <div className="px-2 pt-3 pb-2 text-center">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold text-white mb-1.5"
                          style={{ background: lp.badgeColor }}>{lp.badge}</span>
                        <div className="text-3xl font-serif leading-none mb-0.5" style={{ color: lp.stemColor }}>{lp.stem}</div>
                        <p className="text-[9px]" style={{ color: '#777' }}>{lp.stemLabel}</p>
                        <p className="text-[10px] font-medium" style={{ color: lp.stemColor }}>{lp.pinyin}</p>
                        <p className="text-[9px]" style={{ color: '#666' }}>{lp.element}</p>
                      </div>
                      {/* Earthly Branch */}
                      <div className="px-2 pt-2 pb-2 text-center" style={{ borderTop: '1px solid #3a3a3a' }}>
                        <div className="text-3xl font-serif leading-none mb-0.5" style={{ color: lp.branchColor }}>{lp.branch}</div>
                        <p className="text-[10px] font-medium text-white">{lp.animal}</p>
                        <p className="text-[9px]" style={{ color: '#666' }}>{lp.branchEl}</p>
                      </div>
                      {/* Hidden Stems */}
                      <div className="px-2 pt-2 pb-3 space-y-1.5" style={{ borderTop: '1px solid #3a3a3a' }}>
                        <p className="text-[8px] tracking-wider" style={{ color: '#555' }}>藏干</p>
                        {lp.hidden.map((h, j) => (
                          <div key={j}>
                            <div className="flex items-center gap-1 mb-0.5">
                              <span className="text-xs font-serif" style={{ color: h.bc }}>{h.ch}</span>
                              <span className="text-[9px]" style={{ color: '#888' }}>{h.py}</span>
                              <span className="inline-block px-1 py-0 rounded text-[7px] font-bold text-white ml-auto"
                                style={{ background: h.bc }}>{h.badge}</span>
                            </div>
                            <div className="h-1 rounded-full" style={{ background: '#333' }}>
                              <div className="h-full rounded-full" style={{ width: `${h.pct}%`, background: h.bc, opacity: 0.7 }} />
                            </div>
                            <p className="text-[8px] text-right" style={{ color: '#666' }}>{h.pct}%</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Intelligence Platform</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              Powerful <span className="font-serif italic" style={{ color: C.goldLight }}>BaZi Intelligence</span> Platform
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Compass} title="BaZi Chart Engine"
              desc="Generate your complete Four Pillars chart instantly from birth data. Heavenly Stems, Earthly Branches, and Hidden Stems revealed."
              accent={C.gold} />
            <FeatureCard icon={TrendingUp} title="Luck Pillars Timeline"
              desc="See how 10-year cycles influence your life direction and opportunities. Know when to push forward and when to consolidate."
              accent={C.jade} />
            <FeatureCard icon={BarChart3} title="Monthly Energy Forecast"
              desc="Understand when to act, plan, invest or rest based on monthly energy cycles aligned with your personal chart."
              accent="#4A90D9" />
            <FeatureCard icon={Users} title="10 Profiles Analysis"
              desc="Discover your personal strengths: Director, Performer, Leader, Artist, Friend, Philosopher, Warrior, Pioneer, Diplomat, Guardian."
              accent="#E85D47" />
            <FeatureCard icon={Brain} title="Five Structures Map"
              desc="Reveal whether you are a Connector, Creator, Manager, Supporter, or Thinker through the Five Structures personality framework."
              accent="#C0C0C0" />
            <FeatureCard icon={Calendar} title="Date Selection Intelligence"
              desc="Identify the best days for business decisions, important meetings, investments, and significant life events."
              accent={C.goldLight} />
          </div>
        </div>
      </section>

      {/* ═══════════════ ANCIENT × MODERN ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.jade }}>Philosophy</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
            Ancient System, <span className="font-serif italic" style={{ color: C.goldLight }}>Modern Platform</span>
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#aaa' }}>
            BaZi, also known as the <span className="text-white font-medium">Four Pillars of Destiny</span>, is a classical Chinese system that maps human destiny using the interaction between the Five Elements, Heavenly Stems and Earthly Branches derived from your birth date and time.
          </p>
          <p className="text-base leading-relaxed" style={{ color: '#aaa' }}>
            Our platform transforms this ancient system into a <span className="text-white font-medium">modern analytical dashboard</span>, making destiny insights easier to understand and apply.
          </p>

          {/* Five Elements */}
          <div className="flex justify-center gap-6 mt-12">
            {[
              { el: '木', name: 'Wood', color: C.jade },
              { el: '火', name: 'Fire', color: '#E85D47' },
              { el: '土', name: 'Earth', color: C.gold },
              { el: '金', name: 'Metal', color: '#C0C0C0' },
              { el: '水', name: 'Water', color: '#4A90D9' },
            ].map((e) => (
              <div key={e.name} className="text-center group cursor-default">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-serif mx-auto mb-2 transition-transform group-hover:scale-110"
                  style={{ background: `${e.color}15`, color: e.color, border: `1px solid ${e.color}30` }}>
                  {e.el}
                </div>
                <p className="text-xs" style={{ color: '#888' }}>{e.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SNEAK PEEK CARDS ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="sneak-peek-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Sneak Peek</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              What You'll <span className="font-serif italic" style={{ color: C.goldLight }}>Discover</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Day Master', main: '丁', sub: 'Ding – Yin Fire', el: 'Fire', color: '#E85D47' },
              { label: 'Current Luck Pillar', main: '乙卯', sub: 'Yin Wood Rabbit', el: '2024–2034', color: C.jade },
              { label: 'Monthly Influence', main: '辛卯', sub: 'Yin Metal Rabbit', el: 'March 2026', color: '#C0C0C0' },
              { label: 'Main Profile', main: '正財', sub: 'Director', el: 'Direct Wealth', color: C.gold },
            ].map((card) => (
              <div key={card.label} className="rounded-xl p-5 text-center group hover:-translate-y-1 transition-all duration-500"
                style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-4" style={{ color: '#666' }}>{card.label}</p>
                <div className="text-4xl font-serif mb-2" style={{ color: card.color }}>{card.main}</div>
                <p className="text-white text-sm font-medium">{card.sub}</p>
                <p className="text-xs mt-2" style={{ color: '#777' }}>{card.el}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ COUNTDOWN + WAITLIST ═══════════════ */}
      <section className="relative z-10 py-28" id="waitlist" data-testid="waitlist-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Launching Soon</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-10">
            The Future of <span className="font-serif italic" style={{ color: C.goldLight }}>BaZi Analysis</span>
          </h2>

          <CountdownTimer />

          <p className="mt-10 mb-8 text-base" style={{ color: '#999' }}>
            We are preparing something powerful for the BaZi community.<br />
            Join the waitlist to get early access.
          </p>

          {submitted ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl"
              style={{ background: `${C.jade}15`, border: `1px solid ${C.jade}30`, color: C.jade }}>
              <Star className="w-5 h-5" />
              <span className="font-medium">{submitMsg}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 h-12 text-white border-0"
                style={{ background: C.card, borderColor: C.cardBorder }}
                data-testid="waitlist-email"
              />
              <Button type="submit" disabled={submitting} className="h-12 px-8 font-medium"
                style={{ background: C.gold, color: C.charcoal }}
                data-testid="waitlist-submit">
                {submitting ? 'Joining...' : 'Join Waitlist'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="relative z-10 border-t py-12" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                <Compass className="w-5 h-5" style={{ color: C.gold }} />
                <span className="text-white font-semibold">Bazi.live</span>
              </div>
              <p className="text-xs" style={{ color: '#666' }}>Powered by Life Decoder</p>
            </div>
            <div className="flex items-center gap-6">
              {['About', 'Contact', 'Privacy Policy'].map((link) => (
                <a key={link} href="#" className="text-sm transition-colors hover:text-white" style={{ color: '#777' }}>{link}</a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="mt-8 pt-6 flex flex-wrap justify-center gap-4" style={{ borderTop: '1px solid #ffffff08' }}>
            {[
              { name: 'Stevenyong.live', url: 'https://stevenyong.live' },
              { name: 'Bazi.live', url: 'https://bazi.live' },
              { name: 'QMDJ.live', url: 'https://qmdj.live' },
              { name: 'Fengshuiplotter.live', url: 'https://fengshuiplotter.live' },
              { name: 'Lifecode.live', url: 'https://lifecode.live' },
              { name: 'Lifetunes.live', url: 'https://lifetunes.live' },
              { name: 'Onlinetongshu.com', url: 'https://onlinetongshu.com' },
            ].map((link) => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className="text-xs transition-colors hover:text-white" style={{ color: C.gold }}>
                {link.name}
              </a>
            ))}
          </div>
          <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid #ffffff08' }}>
            <p className="text-xs" style={{ color: '#555' }}>Life Decoder &copy; {new Date().getFullYear()} BaZi.live - All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BaZiComingSoon;
